import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "sonner";
import { Clock, Coffee, CalendarClock, UserX, ListOrdered } from "lucide-react";
import DashboardWrapper from "../../components/Layout/DashboardWrapper";
import { getUserData } from "../../utils/storageUtils";
import { isAdmin, isSecretary, roles } from "../../constants/roles";
import { getUsersByRole } from "../../services/users";
import { getTodayList, reassignDoctor, removeFromQueue } from "../../services/queue";
import { QueueEntryApi, hm, initials } from "../../services/queue/types";
import useQueueStream from "../../hooks/useQueueStream";

const nowHm = () =>
  new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

const QueueSecretaire: React.FC = () => {
  const userData = getUserData();
  const allowed = isSecretary(userData) || isAdmin(userData);

  const queryClient = useQueryClient();
  const [entries, setEntries] = useState<QueueEntryApi[]>([]);
  const [clock, setClock] = useState(nowHm());

  useEffect(() => {
    const handle = window.setInterval(() => setClock(nowHm()), 30000);
    return () => window.clearInterval(handle);
  }, []);

  const listQuery = useQuery(["queue-today-list"], () => getTodayList(), {
    enabled: allowed,
    refetchInterval: 60000,
    onSuccess: (res: any) => {
      if (res?.ok) setEntries((res.data ?? []) as QueueEntryApi[]);
    },
  });

  useQueueStream((queue) => setEntries(queue), allowed, "/queue/stream");

  const { data: doctorsResult } = useQuery(
    ["queue-doctors"],
    () => getUsersByRole(roles.ROLE_DOCTOR, "", 0, 200),
    { enabled: allowed, staleTime: 60000 }
  );
  const doctors: any[] = (doctorsResult as any)?.data?.content || [];

  const invalidate = () => queryClient.invalidateQueries(["queue-today-list"]);

  const reassignMut = useMutation(
    (vars: { entryId: number; doctorId: number }) => reassignDoctor(vars.entryId, vars.doctorId),
    {
      onSuccess: (res: any) => {
        if (res?.ok) {
          toast.success("Médecin réassigné");
          invalidate();
        }
      },
    }
  );

  const removeMut = useMutation((entryId: number) => removeFromQueue(entryId), {
    onSuccess: (res: any) => {
      if (res?.ok) {
        toast.success("Patient retiré de la liste");
        invalidate();
      }
    },
  });

  if (!allowed) {
    return <Navigate to="/" replace />;
  }

  return (
    <DashboardWrapper showSpinner={listQuery.isLoading && entries.length === 0}>
      <div className="font-poppins max-w-[1080px] mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold">Liste d'attente</h1>
          <div className="flex items-center gap-4">
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-success/10 text-xs font-medium"
              style={{ color: "hsl(var(--success))" }}
            >
              <span className="relative flex w-2 h-2">
                <span
                  className="absolute inline-flex w-full h-full rounded-full opacity-75 animate-ping"
                  style={{ background: "hsl(var(--success))" }}
                />
                <span className="relative inline-flex w-2 h-2 rounded-full" style={{ background: "hsl(var(--success))" }} />
              </span>
              Temps réel
            </span>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-secondary text-secondary-foreground text-sm font-medium">
              <Clock className="w-4 h-4" />
              <span>{clock}</span>
            </div>
          </div>
        </div>

        {entries.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-card py-20 flex flex-col items-center text-center">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-secondary mb-4">
              <Coffee className="w-7 h-7 text-muted-foreground" />
            </div>
            <p className="text-lg font-semibold">Liste d'attente vide</p>
            <p className="text-sm text-muted-foreground mt-1 max-w-xs">
              Ajoutez un patient depuis la page Patients ; il apparaîtra ici automatiquement.
            </p>
          </div>
        ) : (
          <section>
            <div className="flex items-center justify-between mb-2">
              <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <ListOrdered className="w-4 h-4" /> Patients en attente — ordre d'arrivée
              </h2>
              <span className="inline-flex items-center justify-center min-w-6 h-6 px-2 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold">
                {entries.length}
              </span>
            </div>

            <div className="rounded-lg border border-border bg-card divide-y divide-border">
              {entries.map((entry, index) => (
                <div key={entry.id} className="flex items-center justify-between px-4 py-3 gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-6 text-center text-sm font-bold text-muted-foreground">{index + 1}</span>
                    <span className="flex items-center justify-center w-10 h-10 rounded-full bg-secondary text-sm font-semibold shrink-0">
                      {initials(entry.patientFirstName, entry.patientLastName)}
                    </span>
                    <div className="min-w-0">
                      <p className="font-medium truncate">
                        {entry.patientFirstName} <span className="uppercase">{entry.patientLastName}</span>
                      </p>
                      <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
                        {entry.walkIn ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-secondary text-secondary-foreground font-medium">
                            Sans RDV
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-medium"
                            style={{ background: "hsl(var(--primary)/0.08)", color: "hsl(var(--primary))" }}
                          >
                            <CalendarClock className="w-3 h-3" /> RDV {hm(entry.appointmentTime)}
                          </span>
                        )}
                        {entry.arrivalTime && <span>Arrivé {hm(entry.arrivalTime)}</span>}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {/* Réassignation : correction du médecin assigné */}
                    <select
                      value={entry.assignedDoctorId ?? ""}
                      disabled={reassignMut.isLoading}
                      onChange={(e) =>
                        reassignMut.mutate({ entryId: entry.id, doctorId: Number(e.target.value) })
                      }
                      className="h-9 max-w-[180px] rounded-md border border-input bg-card px-2 text-sm outline-none focus:ring-2 focus:ring-ring/40"
                      title="Médecin assigné — modifier pour réassigner"
                    >
                      {doctors.map((doc) => (
                        <option key={doc?.id} value={doc?.id}>
                          {doc?.firstName} {doc?.lastName}
                        </option>
                      ))}
                    </select>
                    <button
                      disabled={removeMut.isLoading}
                      onClick={() => removeMut.mutate(entry.id)}
                      title="Retirer de la liste"
                      aria-label="Retirer de la liste"
                      className="flex h-9 w-9 items-center justify-center rounded-md hover:bg-destructive/10 disabled:opacity-50"
                      style={{ color: "hsl(var(--destructive))" }}
                    >
                      <UserX className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <p className="mt-3 text-xs text-muted-foreground flex items-center gap-1.5">
              <ListOrdered className="w-3.5 h-3.5" />
              Ordre d'arrivée (FIFO). Changez le médecin dans la liste déroulante, ou retirez un patient.
            </p>
          </section>
        )}
      </div>
    </DashboardWrapper>
  );
};

export default QueueSecretaire;
